import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiUser3Line, RiMailLine, RiLockLine, RiEyeLine, RiEyeOffLine, RiShieldStarLine, RiArrowLeftLine, RiMailSendLine } from 'react-icons/ri';
import { C } from '../../data/constants';
import { useAuth } from '../../context/AuthContext';
import OtpInput from '../../components/shared/OtpInput';
import { resendSignupOtp } from '../../api/auth.api';
import KitsuMark from '../../components/brand/KitsuMark';

const OTP_LENGTH = 6;

const GOOGLE_SCRIPT_ID = 'google-identity-script';
const REACT_APP_GOOGLE_CLIENT_ID = "817237655307-kh5meria7ukng8rvee12q1fdh3b426i7.apps.googleusercontent.com";

export default function Auth({ mode }) {
  const navigate = useNavigate();
  const { login, signup, verifySignup, googleAuth, authLoading, authError, setAuthError } = useAuth();
  const isLogin = mode === 'login';
  const googleButtonRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);

  /* Signup is two screens now: the form, then the emailed code. `awaiting`
     holds the address we sent to — non-null means show the code screen. */
  const [awaiting, setAwaiting] = useState(null);
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setAuthError('');

    if (isLogin) {
      const result = await login({ email, password });
      if (result.success) navigate('/dashboard');
      return;
    }

    // Step 1: no account yet — this only sends the code.
    const result = await signup({ name, email, password });
    if (result.success) {
      setAwaiting(email);
      setOtp('');
      setCooldown(result.data?.resendCooldownSeconds || 60);
      // The heading already names the address — a banner repeating it here
      // is noise. It's reserved for resend confirmations.
      setNotice('');
    }
  };

  const submitOtp = async (code) => {
    const value = code ?? otp;
    if (value.length !== OTP_LENGTH) return;
    setAuthError('');
    const result = await verifySignup({ email: awaiting, otp: value });
    if (result.success) navigate('/dashboard');
    else setOtp('');
  };

  const handleResend = async () => {
    setAuthError('');
    setNotice('');
    try {
      const res = await resendSignupOtp({ email: awaiting });
      setNotice(res.message);
      setCooldown(res.data?.resendCooldownSeconds || 60);
      setOtp('');
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    if (!response?.credential) {
      setAuthError('Google sign-in did not return a credential.');
      return;
    }

    const result = await googleAuth({ credential: response.credential });
    if (result.success) navigate('/dashboard');
  }, [googleAuth, navigate, setAuthError]);

  useEffect(() => {
    // const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const clientId = "817237655307-kh5meria7ukng8rvee12q1fdh3b426i7.apps.googleusercontent.com";
    if (!clientId || !googleButtonRef.current) return;

    const renderGoogleButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        width: Math.min(googleButtonRef.current.clientWidth, 400),
        text: isLogin ? 'signin_with' : 'signup_with',
        logo_alignment: 'left',
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return undefined;
    }

    let script = document.getElementById(GOOGLE_SCRIPT_ID);
    const onLoad = () => renderGoogleButton();

    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', onLoad, { once: true });
      document.body.appendChild(script);
    } else {
      script.addEventListener('load', onLoad, { once: true });
      if (window.google?.accounts?.id) renderGoogleButton();
    }

    return () => {
      script?.removeEventListener?.('load', onLoad);
    };
  }, [handleGoogleCredential, isLogin]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(249,115,22,0.06), transparent 70%)', pointerEvents: 'none' }} />

      {awaiting ? (
        /* ── Step 2 of signup: prove the inbox exists ── */
        <div className="ks-card anim-pop" style={{ padding: '40px 36px', width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 34, marginBottom: 10, color: C.accent }}><RiMailSendLine /></div>
            <h1 style={{ fontSize: 21, fontWeight: 900, letterSpacing: -0.6, marginBottom: 6 }}>Check your inbox</h1>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
              We sent a {OTP_LENGTH}-digit code to<br />
              <strong style={{ color: C.text }}>{awaiting}</strong>
            </p>
          </div>

          <form onSubmit={e => { e.preventDefault(); submitOtp(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <OtpInput
              value={otp}
              onChange={setOtp}
              length={OTP_LENGTH}
              onComplete={submitOtp}
              disabled={authLoading}
            />

            {authError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.28)', borderRadius: 9, padding: '10px 14px', fontSize: 13, color: C.red, fontWeight: 600 }}>
                {authError}
              </div>
            )}
            {!authError && notice && (
              <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.26)', borderRadius: 9, padding: '10px 14px', fontSize: 12.5, color: C.green, fontWeight: 600 }}>
                {notice}
              </div>
            )}

            <button type="submit" className="btn-primary" disabled={authLoading || otp.length !== OTP_LENGTH}
              style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }}>
              {authLoading ? <div className="spinner" /> : 'Verify & create account'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12.5, color: C.muted }}>
            Didn&apos;t get it?{' '}
            {cooldown > 0 ? (
              <span style={{ color: C.faint }}>Resend in {cooldown}s</span>
            ) : (
              <span style={{ color: C.accent, fontWeight: 700, cursor: 'pointer' }} onClick={handleResend}>
                Send a new code
              </span>
            )}
          </div>

          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <span
              style={{ color: C.faint, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              onClick={() => { setAwaiting(null); setOtp(''); setNotice(''); setAuthError(''); }}
            >
              <RiArrowLeftLine size={13} /> Use a different email
            </span>
          </div>
        </div>
      ) : (
      <div className="ks-card anim-pop" style={{ padding: '40px 36px', width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
            <KitsuMark size={44} animation="pop" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.7, marginBottom: 5 }}>
            {isLogin ? 'Welcome back' : 'Start your journey'}
          </h1>
          <p style={{ color: C.muted, fontSize: 13 }}>
            {isLogin ? 'Sign in to continue learning' : 'Create your free KitsuSpeak account'}
          </p>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1.6, textTransform: 'uppercase', color: C.faint }}>
              Continue with
            </div>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>
            <div ref={googleButtonRef} className="google-auth-slot" />
          {/* <div className="google-auth-shell"></div> */}
              {!REACT_APP_GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              Add `REACT_APP_GOOGLE_CLIENT_ID` to enable Google sign-in.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isLogin && (
            <div style={{ position: 'relative' }}>
              <RiUser3Line size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
              <input className="ks-input" style={{ paddingLeft: 38 }} placeholder="Full name" value={name} onChange={e => setName(e.target.value)} />
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <RiMailLine size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
            <input className="ks-input" style={{ paddingLeft: 38 }} placeholder="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>

          <div style={{ position: 'relative' }}>
            <RiLockLine size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
            <input
              className="ks-input"
              style={{ paddingLeft: 38, paddingRight: 42 }}
              placeholder="Password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <div onClick={() => setShowPass(s => !s)} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted, cursor: 'pointer' }}>
              {showPass ? <RiEyeOffLine size={15} /> : <RiEyeLine size={15} />}
            </div>
          </div>

          {isLogin && (
            <div style={{ textAlign: 'right', marginTop: -4 }}>
              <span
                style={{ color: C.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </span>
            </div>
          )}

          {/* Error message from API */}
          {authError && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.28)', borderRadius: 9, padding: '10px 14px', fontSize: 13, color: C.red, fontWeight: 600 }}>
              {authError}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15, marginTop: 4 }}
            disabled={authLoading}
          >
            {authLoading ? <div className="spinner" /> : (isLogin ? 'Sign In' : 'Send verification code')}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, color: C.faint, fontSize: 12 }}>
          <RiShieldStarLine size={14} />
          Secure login. Your Google email is verified before we create or link an account.
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: C.muted }}>
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <span style={{ color: C.accent, cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate(isLogin ? '/signup' : '/login')}>
            {isLogin ? 'Sign up free' : 'Sign in'}
          </span>
        </div>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <span style={{ color: C.faint, fontSize: 12, cursor: 'pointer' }} onClick={() => navigate('/')}>← Back to home</span>
        </div>
      </div>
      )}
    </div>
  );
}
